/**
 * SmartMealsModal - Extracted from App.js Phase 3
 * Full meal planning modal with Recommended and Fit Remaining Macros modes
 * Includes: meal plan generation, fit macros, weekly meal prep, batch cook, recipes
 */
import React from 'react';
import { toast } from 'sonner';
import { AITransparencyNotice } from '../ui/UtilityComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SmartMealsModal = ({
  isOpen,
  onClose,
  userId,
  userProfile,
  aiNutritionGoals,
  mealPlanPreferences,
  setMealPlanPreferences,
  mealPlanResult,
  setMealPlanResult,
  mealPlannerFitMode,
  setMealPlannerFitMode,
  analyzing,
  setAnalyzing,
  fitMacroResults,
  setFitMacroResults,
  fitMacroLoading,
  setFitMacroLoading,
  loggingFitMealIdx,
  setLoggingFitMealIdx,
  dailyGoal,
  targetProtein,
  targetFat,
  targetCarbs,
  todayTotals,
  ifFastingActive,
  loadTodayMeals,
  addMealPlanToToday,
  markMealPlannerExitWithoutGenerate,
  setMealPlannerJustExited,
  openSmartGoalsGated,
  isPremium,
  formatMealInstructions,
  savingSmartMealId,
  handleSaveSmartMeal
}) => {
  if (!isOpen) return null;

  return (
          <div className="modal-overlay" onClick={() => {
            // Track exit without generating for RULE E
            if (!mealPlanResult) {
              markMealPlannerExitWithoutGenerate();
              setMealPlannerJustExited(true);
            }
            onClose();
          }}>
            <div className="modal-content meal-planner-modal" onClick={(e) => e.stopPropagation()}>
              <h2 className="heading-3">🍽️ SmartMeals</h2>
              <p className="modal-description">Get personalized meal plans with recipes based on your goals</p>
              <div className="premium-badge">Premium Feature</div>

              {/* Recommended vs Fit Remaining Macros Toggle */}
              <div style={{
                display: 'flex',
                gap: '6px',
                marginBottom: '12px',
                padding: '3px',
                background: 'rgba(255,255,255,0.04)',
                borderRadius: '10px',
                border: '1px solid rgba(255,255,255,0.06)'
              }}>
                <button
                  data-testid="smartmeals-recommended-tab"
                  onClick={() => { setMealPlannerFitMode(false); setFitMacroResults(null); }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: 'none',
                    borderRadius: '8px',
                    background: !mealPlannerFitMode ? 'rgba(102, 126, 234, 0.2)' : 'transparent',
                    color: !mealPlannerFitMode ? '#667eea' : 'var(--text-secondary)',
                    fontWeight: '600',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Recommended
                </button>
                <button
                  data-testid="smartmeals-fit-macros-tab"
                  onClick={() => {
                    setMealPlannerFitMode(true);
                    setFitMacroResults(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: 'none',
                    borderRadius: '8px',
                    background: mealPlannerFitMode ? 'linear-gradient(135deg, rgba(102,126,234,0.25) 0%, rgba(118,75,162,0.25) 100%)' : 'transparent',
                    color: mealPlannerFitMode ? '#667eea' : 'var(--text-secondary)',
                    fontWeight: '600',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Fit Remaining Macros
                </button>
              </div>

              {/* Fit Remaining Macros Mode */}
              {mealPlannerFitMode ? (
                <div data-testid="fit-remaining-macros-view">
                  {/* Fasting check */}
                  {ifFastingActive ? (
                    <div style={{
                      padding: '24px 16px',
                      textAlign: 'center',
                      background: 'rgba(255, 107, 107, 0.08)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 107, 107, 0.2)',
                      marginBottom: '16px'
                    }}>
                      <span style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }}>⏳</span>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: 'white' }}>
                        You're currently fasting
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                        Save this for your eating window
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Remaining macros header */}
                      {(() => {
                        const calRemaining = Math.max((dailyGoal || 2000) - (todayTotals?.calories || 0), 0);
                        const protRemaining = Math.max((targetProtein || 150) - (todayTotals?.protein || 0), 0);
                        const fatRemaining = Math.max((targetFat || 65) - (todayTotals?.fat || 0), 0);
                        const carbsRemaining = Math.max((targetCarbs || 250) - (todayTotals?.carbs || 0), 0);
                        const fatOverLimit = (todayTotals?.fat || 0) > (targetFat || 65) * 1.1;

                        return (
                          <>
                            <div style={{
                              padding: '14px 16px',
                              background: 'rgba(102, 126, 234, 0.08)',
                              borderRadius: '12px',
                              border: '1px solid rgba(102, 126, 234, 0.15)',
                              marginBottom: '16px'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: 'white' }}>
                                  ~{Math.round(calRemaining)} kcal left today
                                </span>
                                <span style={{ fontSize: '12px', color: '#667eea', fontWeight: '600' }}>
                                  ~{Math.round(protRemaining)}g protein needed
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                                <span>Carbs: {Math.round(carbsRemaining)}g</span>
                                <span>Fat: {Math.round(fatRemaining)}g</span>
                                {fatOverLimit && (
                                  <span style={{ color: '#ffb74d', fontWeight: '600' }}>⚠️ Keep fats low</span>
                                )}
                              </div>
                            </div>

                            {/* Results */}
                            {fitMacroLoading ? (
                              <div style={{ textAlign: 'center', padding: '32px 0', color: 'rgba(255,255,255,0.4)' }}>
                                <div style={{ fontSize: '13px' }}>Finding meals that fit your macros...</div>
                              </div>
                            ) : fitMacroResults?.meals?.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                                {fitMacroResults.meals.map((meal, i) => (
                                  <div key={i} data-testid="fit-macro-meal-card" style={{
                                    padding: '12px 14px',
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                    borderRadius: '12px'
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                      <span style={{ fontSize: '20px' }}>{meal.emoji || '🍽️'}</span>
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'white' }}>{meal.name}</div>
                                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '1px' }}>{meal.note}</div>
                                      </div>
                                      {meal.fit_score >= 8 && (
                                        <span style={{
                                          padding: '2px 8px', borderRadius: '8px',
                                          background: 'rgba(56, 239, 125, 0.15)', color: '#38ef7d',
                                          fontSize: '10px', fontWeight: '600'
                                        }}>Best Fit</span>
                                      )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <div style={{ flex: 1, display: 'flex', gap: '10px', fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                                        <span>{meal.calories} kcal</span>
                                        <span>{meal.protein}g protein</span>
                                        <span>{meal.fat}g fat</span>
                                        <span>{meal.carbs}g carbs</span>
                                      </div>
                                      <button
                                        data-testid={`fit-macro-log-btn-${i}`}
                                        disabled={loggingFitMealIdx === i}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setLoggingFitMealIdx(i);
                                          try {
                                            const tzOffset = Math.round(-new Date().getTimezoneOffset() / 60);
                                            const resp = await fetch(`${BACKEND_URL}/api/meals/log-recipe`, {
                                              method: 'POST',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({
                                                user_id: userId,
                                                recipe: {
                                                  title: meal.name,
                                                  macros: {
                                                    calories: meal.calories || 0,
                                                    protein_g: meal.protein || 0,
                                                    carbs_g: meal.carbs || 0,
                                                    fat_g: meal.fat || 0
                                                  },
                                                  meal_type: (() => {
                                                    const h = new Date().getHours();
                                                    if (h >= 5 && h < 11) return 'Breakfast';
                                                    if (h >= 11 && h < 15) return 'Lunch';
                                                    if (h >= 15 && h < 18) return 'Snack';
                                                    if (h >= 18 && h < 22) return 'Dinner';
                                                    return 'Other';
                                                  })()
                                                },
                                                source: 'fit_macros',
                                                tz_offset: tzOffset
                                              })
                                            });
                                            if (resp.ok) {
                                              toast.success(`${meal.emoji || '🍽️'} ${meal.name} logged!`);
                                              await loadTodayMeals(userId);
                                              onClose();
                                              setFitMacroResults(null);
                                              setMealPlannerFitMode(false);
                                            } else {
                                              toast.error('Failed to log meal');
                                            }
                                          } catch (err) {
                                            console.error('[FitMacros] Log error:', err);
                                            toast.error('Failed to log meal');
                                          } finally {
                                            setLoggingFitMealIdx(null);
                                          }
                                        }}
                                        style={{
                                          padding: '6px 12px',
                                          background: loggingFitMealIdx === i
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'rgba(102, 126, 234, 0.2)',
                                          border: '1px solid rgba(102, 126, 234, 0.3)',
                                          borderRadius: '8px',
                                          color: '#667eea',
                                          fontSize: '11px',
                                          fontWeight: '600',
                                          cursor: loggingFitMealIdx === i ? 'not-allowed' : 'pointer',
                                          whiteSpace: 'nowrap',
                                          transition: 'all 0.15s ease'
                                        }}
                                      >
                                        {loggingFitMealIdx === i ? 'Logging...' : 'Log This'}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', margin: '0 0 12px' }}>
                                  Tap below to find meals matching your remaining macros
                                </p>
                              </div>
                            )}

                            {/* Generate / Refresh button */}
                            <button
                              data-testid="fit-macros-generate-btn"
                              disabled={fitMacroLoading}
                              onClick={async () => {
                                setFitMacroLoading(true);
                                try {
                                  const resp = await fetch(`${BACKEND_URL}/api/health-intelligence/fit-remaining-meals`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      user_id: userId,
                                      calories_remaining: calRemaining,
                                      protein_remaining: protRemaining,
                                      fat_remaining: fatRemaining,
                                      carbs_remaining: carbsRemaining,
                                      fat_over_limit: fatOverLimit,
                                      diet_type: userProfile?.dietary_preference || '',
                                      allergies: [
                                        ...(userProfile?.food_allergies?.filter(a => a && a !== 'none') || []),
                                        ...(aiNutritionGoals?.intake_data?.food_allergies?.filter(a => a && a !== 'none') || [])
                                      ].filter((v, idx, arr) => arr.indexOf(v) === idx)
                                    })
                                  });
                                  if (resp.ok) {
                                    const data = await resp.json();
                                    setFitMacroResults(data);
                                  }
                                } catch (err) {
                                  console.error('[FitMacros] Error:', err);
                                } finally {
                                  setFitMacroLoading(false);
                                }
                              }}
                              style={{
                                width: '100%',
                                padding: '12px',
                                background: fitMacroLoading
                                  ? 'rgba(255,255,255,0.05)'
                                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                borderRadius: '10px',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: fitMacroLoading ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {fitMacroLoading ? 'Finding meals...' : fitMacroResults ? '🔄 Refresh Suggestions' : '🎯 Find Meals That Fit'}
                            </button>
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
              ) : (
              <>

              {/* Mode Selector - Regular or Weekly Meal Prep */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '20px',
                padding: '4px',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px'
              }}>
                <button
                  onClick={() => setMealPlanPreferences({...mealPlanPreferences, mode: 'regular'})}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    borderRadius: '8px',
                    background: (mealPlanPreferences.mode || 'regular') === 'regular' 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                      : 'transparent',
                    color: (mealPlanPreferences.mode || 'regular') === 'regular' ? 'white' : 'var(--text-secondary)',
                    fontWeight: '600',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  🍽️ Daily Plan
                </button>
                <button
                  onClick={() => setMealPlanPreferences({...mealPlanPreferences, mode: 'meal_prep'})}
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: 'none',
                    borderRadius: '8px',
                    background: mealPlanPreferences.mode === 'meal_prep' 
                      ? 'linear-gradient(135deg, #ff9800 0%, #ff5722 100%)' 
                      : 'transparent',
                    color: mealPlanPreferences.mode === 'meal_prep' ? 'white' : 'var(--text-secondary)',
                    fontWeight: '600',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  📦 Weekly Meal Prep
                </button>
              </div>

              {/* Weekly Meal Prep Mode Description */}
              {mealPlanPreferences.mode === 'meal_prep' && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 87, 34, 0.1) 100%)',
                  border: '1px solid rgba(255, 152, 0, 0.3)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#ff9800' }}>
                    📦 Weekly Meal Prep Mode
                  </h4>
                  <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Batch cooking for the week. Includes: repeating meals, bulk quantities, grocery list, and reheating notes.
                  </p>
                  
                  {/* NEW: Prep Style Toggle - Fresh Daily vs Batch Cook + Freeze */}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                      Prep Style
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setMealPlanPreferences({...mealPlanPreferences, prepStyle: 'fresh_daily'})}
                        style={{
                          flex: 1,
                          padding: '12px 8px',
                          border: 'none',
                          borderRadius: '8px',
                          background: (mealPlanPreferences.prepStyle || 'fresh_daily') === 'fresh_daily' 
                            ? 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)' 
                            : 'rgba(255,255,255,0.08)',
                          color: (mealPlanPreferences.prepStyle || 'fresh_daily') === 'fresh_daily' ? 'white' : 'var(--text-secondary)',
                          fontWeight: '600',
                          fontSize: '11px',
                          cursor: 'pointer',
                          textAlign: 'center'
                        }}
                      >
                        🥗 Fresh Daily
                        <span style={{ display: 'block', fontSize: '9px', fontWeight: '400', marginTop: '2px', opacity: 0.9 }}>
                          Variety each day
                        </span>
                      </button>
                      <button
                        onClick={() => setMealPlanPreferences({...mealPlanPreferences, prepStyle: 'batch_freeze'})}
                        style={{
                          flex: 1,
                          padding: '12px 8px',
                          border: 'none',
                          borderRadius: '8px',
                          background: mealPlanPreferences.prepStyle === 'batch_freeze' 
                            ? 'linear-gradient(135deg, #2196F3 0%, #03A9F4 100%)' 
                            : 'rgba(255,255,255,0.08)',
                          color: mealPlanPreferences.prepStyle === 'batch_freeze' ? 'white' : 'var(--text-secondary)',
                          fontWeight: '600',
                          fontSize: '11px',
                          cursor: 'pointer',
                          textAlign: 'center'
                        }}
                      >
                        ❄️ Batch + Freeze
                        <span style={{ display: 'block', fontSize: '9px', fontWeight: '400', marginTop: '2px', opacity: 0.9 }}>
                          Cook once, freeze portions
                        </span>
                      </button>
                    </div>
                  </div>
                  
                  {/* Batch + Freeze Description */}
                  {mealPlanPreferences.prepStyle === 'batch_freeze' && (
                    <div style={{
                      background: 'rgba(33, 150, 243, 0.1)',
                      border: '1px solid rgba(33, 150, 243, 0.3)',
                      borderRadius: '8px',
                      padding: '10px 12px',
                      marginBottom: '12px',
                      fontSize: '11px',
                      color: 'var(--text-secondary)'
                    }}>
                      <strong style={{ color: '#2196F3' }}>❄️ Batch Cook + Freeze Friendly</strong><br/>
                      3-5 freezer-friendly recipes with storage instructions, reheat methods, and a detailed prep day plan. Perfect for busy schedules!
                    </div>
                  )}
                  
                  {/* Preset Menu Selection */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setMealPlanPreferences({
                        ...mealPlanPreferences, 
                        prepPreset: 'lean_bulk',
                        dietaryRestrictions: ['High-Protein']
                      })}
                      style={{
                        flex: 1,
                        minWidth: '120px',
                        padding: '12px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        background: mealPlanPreferences.prepPreset === 'lean_bulk' 
                          ? '#4caf50' 
                          : 'rgba(255,255,255,0.08)',
                        color: mealPlanPreferences.prepPreset === 'lean_bulk' ? 'white' : 'var(--text-secondary)',
                        fontWeight: '600',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      💪 Lean Bulk
                      <span style={{ display: 'block', fontSize: '10px', fontWeight: '400', marginTop: '2px' }}>
                        High carb / High protein
                      </span>
                    </button>
                    <button
                      onClick={() => setMealPlanPreferences({
                        ...mealPlanPreferences, 
                        prepPreset: 'clean_cut',
                        dietaryRestrictions: ['High-Protein', 'Low-Carb']
                      })}
                      style={{
                        flex: 1,
                        minWidth: '120px',
                        padding: '12px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        background: mealPlanPreferences.prepPreset === 'clean_cut' 
                          ? '#f44336' 
                          : 'rgba(255,255,255,0.08)',
                        color: mealPlanPreferences.prepPreset === 'clean_cut' ? 'white' : 'var(--text-secondary)',
                        fontWeight: '600',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      🔥 Clean Cut
                      <span style={{ display: 'block', fontSize: '10px', fontWeight: '400', marginTop: '2px' }}>
                        High protein / Controlled carb
                      </span>
                    </button>
                  </div>
                  
                  {/* Batch Size */}
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                      Batch Size (days)
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[3, 5, 7].map(days => (
                        <button
                          key={days}
                          onClick={() => setMealPlanPreferences({...mealPlanPreferences, batchDays: days})}
                          style={{
                            flex: 1,
                            padding: '10px',
                            border: 'none',
                            borderRadius: '6px',
                            background: (mealPlanPreferences.batchDays || 5) === days 
                              ? '#667eea' 
                              : 'rgba(255,255,255,0.08)',
                            color: (mealPlanPreferences.batchDays || 5) === days ? 'white' : 'var(--text-secondary)',
                            fontWeight: '600',
                            fontSize: '13px',
                            cursor: 'pointer'
                          }}
                        >
                          {days} days
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {!mealPlanResult ? (
                <div className="meal-plan-form">
                  <div className="form-group">
                    <label>Preferred Cuisines (select multiple)</label>
                    <div className="cuisine-chips">
                      {['Italian', 'Chinese', 'Indian', 'Mexican', 'Japanese', 'Thai', 'Mediterranean', 'American', 'Korean', 'French', 'Vietnamese', 'Middle Eastern', 'Eastern European', 'Western European', 'African', 'Latin American'].map(cuisine => (
                        <button
                          key={cuisine}
                          className={`chip ${mealPlanPreferences.cuisines.includes(cuisine) ? 'chip-active' : ''}`}
                          onClick={() => {
                            const cuisines = mealPlanPreferences.cuisines.includes(cuisine)
                              ? mealPlanPreferences.cuisines.filter(c => c !== cuisine)
                              : [...mealPlanPreferences.cuisines, cuisine];
                            setMealPlanPreferences({...mealPlanPreferences, cuisines});
                          }}
                        >
                          {cuisine}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Dietary Preferences</label>
                    <div className="cuisine-chips">
                      {['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Low-Carb', 'High-Protein', 'Keto', 'Paleo', 'Halal', 'Kosher'].map(diet => (
                        <button
                          key={diet}
                          className={`chip ${mealPlanPreferences.dietaryRestrictions.includes(diet) ? 'chip-active' : ''}`}
                          onClick={() => {
                            const restrictions = mealPlanPreferences.dietaryRestrictions.includes(diet)
                              ? mealPlanPreferences.dietaryRestrictions.filter(d => d !== diet)
                              : [...mealPlanPreferences.dietaryRestrictions, diet];
                            setMealPlanPreferences({...mealPlanPreferences, dietaryRestrictions: restrictions});
                          }}
                        >
                          {diet}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Meals Per Day</label>
                    <select 
                      value={mealPlanPreferences.mealsPerDay}
                      onChange={(e) => setMealPlanPreferences({...mealPlanPreferences, mealsPerDay: parseInt(e.target.value)})}
                      className="search-input"
                      style={{ cursor: 'pointer' }}
                    >
                      <option value="1">1 meal (OMAD)</option>
                      <option value="2">2 meals</option>
                      <option value="3">3 meals</option>
                      <option value="4">4 meals</option>
                      <option value="5">5 meals + snacks</option>
                      <option value="6">6 meals (3 main + 3 snacks)</option>
                      <option value="7">7 meals (grazing style)</option>
                      <option value="8">8 meals (bodybuilder split)</option>
                    </select>
                    {mealPlanPreferences.mealsPerDay >= 5 && (
                      <p style={{ 
                        fontSize: '11px', 
                        color: 'var(--text-secondary)', 
                        marginTop: '6px',
                        padding: '8px',
                        background: 'rgba(139, 195, 74, 0.1)',
                        borderRadius: '6px'
                      }}>
                        💡 Snacks = grab-and-go items: Greek yogurt, nuts, protein bars, fruit, cheese sticks, etc.
                      </p>
                    )}
                  </div>

                  {/* SmartGoals Integration Section */}
                  <div className="form-group" style={{ 
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)', 
                    padding: '16px', 
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 126, 234, 0.3)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          id="useSmartGoals"
                          checked={mealPlanPreferences.useSmartGoals}
                          onChange={(e) => {
                            const useGoals = e.target.checked;
                            if (useGoals && aiNutritionGoals?.intake_complete) {
                              // Apply SmartGoals settings
                              const intake = aiNutritionGoals.intake_data || {};
                              setMealPlanPreferences({
                                ...mealPlanPreferences, 
                                useSmartGoals: true,
                                mealsPerDay: intake.meals_per_day === 'custom' 
                                  ? (intake.custom_meals_count || 3) 
                                  : parseInt(intake.meals_per_day) || 3,
                                optimizeForBodyType: !!intake.body_type,
                                bodyType: intake.body_type || '',
                                intermittentFasting: intake.if_preference && intake.if_preference !== 'none',
                                fastingSchedule: intake.if_preference === '16_8' ? '16:8' 
                                  : intake.if_preference === '18_6' ? '18:6'
                                  : intake.if_preference === '20_4' ? '20:4' : ''
                              });
                            } else {
                              setMealPlanPreferences({
                                ...mealPlanPreferences, 
                                useSmartGoals: false
                              });
                            }
                          }}
                          style={{ width: '18px', height: '18px', marginRight: '10px', cursor: 'pointer' }}
                        />
                        <label htmlFor="useSmartGoals" style={{ 
                          cursor: 'pointer', 
                          fontWeight: '600',
                          fontSize: '14px',
                          color: 'var(--text-primary)'
                        }}>
                          🎯 Use SmartGoals Settings
                        </label>
                      </div>
                      {!aiNutritionGoals?.intake_complete && (
                        <button
                          type="button"
                          onClick={() => openSmartGoalsGated('button')}
                          style={{
                            padding: '6px 12px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '6px',
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Setup SmartGoals
                        </button>
                      )}
                    </div>
                    
                    {mealPlanPreferences.useSmartGoals && aiNutritionGoals?.intake_complete ? (
                      <div style={{ 
                        background: 'rgba(0,0,0,0.2)', 
                        padding: '12px', 
                        borderRadius: '8px'
                      }}>
                        <p style={{ 
                          fontSize: '12px', 
                          color: '#38ef7d', 
                          margin: '0 0 8px 0',
                          fontWeight: '600'
                        }}>
                          ✓ SmartGoals Applied
                        </p>
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: '6px',
                          fontSize: '11px',
                          color: 'var(--text-secondary)'
                        }}>
                          {aiNutritionGoals.intake_data?.body_type && (
                            <span style={{ 
                              background: 'rgba(102, 126, 234, 0.2)', 
                              padding: '2px 8px', 
                              borderRadius: '4px' 
                            }}>
                              💪 {aiNutritionGoals.intake_data.body_type}
                            </span>
                          )}
                          {aiNutritionGoals.intake_data?.if_preference && aiNutritionGoals.intake_data.if_preference !== 'none' && (
                            <span style={{ 
                              background: 'rgba(255, 152, 0, 0.2)', 
                              padding: '2px 8px', 
                              borderRadius: '4px' 
                            }}>
                              ⏰ {aiNutritionGoals.intake_data.if_preference.replace('_', ':')} IF
                            </span>
                          )}
                          {aiNutritionGoals.intake_data?.eating_window && (
                            <span style={{ 
                              background: 'rgba(56, 239, 125, 0.2)', 
                              padding: '2px 8px', 
                              borderRadius: '4px' 
                            }}>
                              🍽️ {aiNutritionGoals.intake_data.eating_window} window
                            </span>
                          )}
                          {aiNutritionGoals.intake_data?.work_schedule_type && aiNutritionGoals.intake_data.work_schedule_type !== 'standard_day' && (
                            <span style={{ 
                              background: 'rgba(147, 51, 234, 0.2)', 
                              padding: '2px 8px', 
                              borderRadius: '4px' 
                            }}>
                              🌙 {aiNutritionGoals.intake_data.work_schedule_type.replace(/_/g, ' ')}
                            </span>
                          )}
                          {aiNutritionGoals.intake_data?.goal_timeline_weeks && (
                            <span style={{ 
                              background: 'rgba(76, 175, 80, 0.2)', 
                              padding: '2px 8px', 
                              borderRadius: '4px' 
                            }}>
                              📅 {aiNutritionGoals.intake_data.goal_timeline_weeks} week goal
                            </span>
                          )}
                        </div>
                        
                        {/* Medical Conditions & Allergies Section */}
                        {(aiNutritionGoals.intake_data?.medical_conditions?.length > 0 || 
                          aiNutritionGoals.intake_data?.food_allergies?.length > 0) && (
                          <div style={{ 
                            marginTop: '10px',
                            padding: '10px',
                            background: 'rgba(239, 68, 68, 0.08)',
                            borderRadius: '6px',
                            border: '1px solid rgba(239, 68, 68, 0.2)'
                          }}>
                            <p style={{ 
                              fontSize: '11px', 
                              color: '#ef4444', 
                              margin: '0 0 6px 0',
                              fontWeight: '600'
                            }}>
                              🏥 Health Profile Active
                            </p>
                            <div style={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: '4px',
                              fontSize: '10px'
                            }}>
                              {aiNutritionGoals.intake_data?.medical_conditions?.filter(c => c !== 'none').map((condition, i) => (
                                <span key={`med-${i}`} style={{ 
                                  background: 'rgba(239, 68, 68, 0.15)', 
                                  padding: '2px 6px', 
                                  borderRadius: '4px',
                                  color: '#ef4444'
                                }}>
                                  {condition.replace(/_/g, ' ')}
                                </span>
                              ))}
                              {aiNutritionGoals.intake_data?.food_allergies?.filter(a => a !== 'none').map((allergy, i) => (
                                <span key={`allergy-${i}`} style={{ 
                                  background: 'rgba(251, 146, 60, 0.15)', 
                                  padding: '2px 6px', 
                                  borderRadius: '4px',
                                  color: '#fb923c'
                                }}>
                                  ⚠️ {allergy.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                            <p style={{ 
                              fontSize: '9px', 
                              color: 'var(--text-muted)', 
                              margin: '6px 0 0 0',
                              fontStyle: 'italic'
                            }}>
                              Meals will avoid ingredients that conflict with your health profile
                            </p>
                          </div>
                        )}

                        {/* Medications & Supplements Section */}
                        {(aiNutritionGoals.intake_data?.medications?.filter(m => m && m !== 'none')?.length > 0 || 
                          aiNutritionGoals.intake_data?.supplements?.filter(s => s && s !== 'none')?.length > 0) && (
                          <div data-testid="smartgoals-meds-supps" style={{ 
                            marginTop: '8px',
                            padding: '10px',
                            background: 'rgba(102, 126, 234, 0.08)',
                            borderRadius: '6px',
                            border: '1px solid rgba(102, 126, 234, 0.2)'
                          }}>
                            <p style={{ 
                              fontSize: '11px', 
                              color: '#667eea', 
                              margin: '0 0 6px 0',
                              fontWeight: '600'
                            }}>
                              💊 Medication & Supplement Awareness
                            </p>
                            <div style={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: '4px',
                              fontSize: '10px'
                            }}>
                              {aiNutritionGoals.intake_data?.medications?.filter(m => m && m !== 'none').map((med, i) => (
                                <span key={`rx-${i}`} style={{ 
                                  background: 'rgba(102, 126, 234, 0.15)', 
                                  padding: '2px 6px', 
                                  borderRadius: '4px',
                                  color: '#667eea'
                                }}>
                                  {med.replace(/_/g, ' ')}
                                </span>
                              ))}
                              {aiNutritionGoals.intake_data?.supplements?.filter(s => s && s !== 'none').map((supp, i) => (
                                <span key={`supp-${i}`} style={{ 
                                  background: 'rgba(56, 239, 125, 0.12)', 
                                  padding: '2px 6px', 
                                  borderRadius: '4px',
                                  color: '#38ef7d'
                                }}>
                                  {supp.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                            <p style={{ 
                              fontSize: '9px', 
                              color: 'var(--text-muted)', 
                              margin: '6px 0 0 0',
                              fontStyle: 'italic'
                            }}>
                              Meals will account for food-drug interactions
                            </p>
                          </div>
                        )}
                        
                        <button
                          type="button"
                          onClick={() => openSmartGoalsGated('button')}
                          style={{
                            marginTop: '8px',
                            background: 'transparent',
                            border: 'none',
                            color: '#667eea',
                            fontSize: '11px',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                        >
                          Edit SmartGoals
                        </button>
                      </div>
                    ) : (
                      <p style={{ 
                        fontSize: '12px', 
                        color: 'var(--text-secondary)', 
                        margin: 0,
                        lineHeight: '1.5'
                      }}>
                        {aiNutritionGoals?.intake_complete 
                          ? 'Enable to use your SmartGoals settings (body type, IF schedule, eating window, work schedule) for better meal plan optimization.'
                          : 'Complete SmartGoals setup to unlock personalized meal planning with body type optimization, IF scheduling, and more.'}
                      </p>
                    )}
                  </div>

                  <button 
                    className="btn-primary full-width"
                    onClick={async () => {
                      if (!isPremium) {
                        toast.error('Meal Planning is a Premium feature. Upgrade to unlock!');
                        return;
                      }
                      
                      setAnalyzing(true);
                      try {
                        // Start async generation
                        const startResponse = await fetch(`${BACKEND_URL}/api/meal-planner/start`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            user_id: userId,
                            preferences: mealPlanPreferences
                          })
                        });
                        
                        if (!startResponse.ok) {
                          const errorData = await startResponse.json().catch(() => ({}));
                          const errorMessage = errorData.detail || 'Failed to start meal plan generation.';
                          toast.error(errorMessage, { duration: 6000 });
                          return;
                        }
                        
                        const startData = await startResponse.json();
                        const taskId = startData.task_id;
                        
                        // Poll for completion (max 60 seconds)
                        const maxAttempts = 30;
                        const pollInterval = 2000; // 2 seconds
                        
                        for (let attempt = 0; attempt < maxAttempts; attempt++) {
                          await new Promise(resolve => setTimeout(resolve, pollInterval));
                          
                          const statusResponse = await fetch(`${BACKEND_URL}/api/meal-planner/status/${taskId}`);
                          if (!statusResponse.ok) {
                            console.error('Poll status error:', statusResponse.status);
                            continue;
                          }
                          const statusData = await statusResponse.json();
                          
                          if (statusData.status === 'completed') {
                            if (statusData.result && statusData.result.meals) {
                              setMealPlanResult(statusData.result);
                            } else {
                              toast.error('Generated meal plan was empty. Please try again.', { duration: 5000 });
                            }
                            return;
                          } else if (statusData.status === 'failed') {
                            const errMsg = statusData.error || 'Failed to generate meal plan.';
                            // Check for budget-related errors
                            if (errMsg.toLowerCase().includes('budget') || errMsg.toLowerCase().includes('exceeded')) {
                              toast.error('AI budget limit reached. Please add balance in Profile → Universal Key → Add Balance.', { duration: 8000 });
                            } else {
                              toast.error(errMsg, { duration: 6000 });
                            }
                            return;
                          }
                          // Still processing, continue polling...
                        }
                        
                        // Timed out
                        toast.error('Meal plan generation is taking longer than expected. Please try again.', { duration: 5000 });
                        
                      } catch (error) {
                        console.error('Error generating meal plan:', error);
                        toast.error('Failed to generate meal plan. Please check your connection and try again.', { duration: 5000 });
                      } finally {
                        setAnalyzing(false);
                      }
                    }}
                    disabled={analyzing}
                  >
                    {analyzing ? '🤖 Generating Plan...' : '🤖 Generate Meal Plan'}
                  </button>
                </div>
              ) : (
                <div className="meal-plan-results">
                  <h3 className="result-title">
                    {mealPlanResult.plan_type === 'weekly_meal_prep' 
                      ? `🥗 ${mealPlanResult.batch_days}-Day Meal Prep Plan` 
                      : 'Your Personalized Meal Plan'}
                  </h3>
                  
                  {/* Weekly Meal Prep Summary Banner */}
                  {mealPlanResult.plan_type === 'weekly_meal_prep' && mealPlanResult.prep_summary && (
                    <div style={{
                      marginTop: '16px',
                      marginBottom: '20px',
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(139, 195, 74, 0.1) 100%)',
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                      borderRadius: '12px'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px',
                        marginBottom: '12px'
                      }}>
                        <span style={{ fontSize: '24px' }}>📋</span>
                        <span style={{ fontSize: '16px', fontWeight: '700', color: '#4CAF50' }}>
                          Prep Summary
                        </span>
                        <span style={{
                          fontSize: '11px',
                          padding: '3px 8px',
                          background: mealPlanResult.prep_summary.prep_goal === 'lean_bulk' 
                            ? 'rgba(102, 126, 234, 0.2)' 
                            : 'rgba(244, 67, 54, 0.2)',
                          color: mealPlanResult.prep_summary.prep_goal === 'lean_bulk' 
                            ? '#667eea' 
                            : '#f44336',
                          borderRadius: '4px',
                          fontWeight: '600',
                          marginLeft: 'auto'
                        }}>
                          {mealPlanResult.prep_summary.prep_goal === 'lean_bulk' ? '💪 LEAN BULK' : '🔥 CLEAN CUT'}
                        </span>
                      </div>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: '12px',
                        textAlign: 'center'
                      }}>
                        <div style={{ 
                          padding: '12px', 
                          background: 'rgba(76, 175, 80, 0.15)', 
                          borderRadius: '10px',
                          border: '1px solid rgba(76, 175, 80, 0.3)'
                        }}>
                          <div style={{ fontSize: '22px', fontWeight: '700', color: '#4CAF50' }}>
                            {mealPlanResult.prep_summary.total_meals}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500' }}>Total Meals</div>
                        </div>
                        <div style={{ 
                          padding: '12px', 
                          background: 'rgba(102, 126, 234, 0.15)', 
                          borderRadius: '10px',
                          border: '1px solid rgba(102, 126, 234, 0.3)'
                        }}>
                          <div style={{ fontSize: '22px', fontWeight: '700', color: '#667eea' }}>
                            {mealPlanResult.prep_summary.recipes_reused_count}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500' }}>Core Recipes</div>
                        </div>
                        <div style={{ 
                          padding: '12px', 
                          background: 'rgba(255, 152, 0, 0.15)', 
                          borderRadius: '10px',
                          border: '1px solid rgba(255, 152, 0, 0.3)'
                        }}>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#ff9800' }}>
                            {mealPlanResult.prep_summary.prep_time_estimate}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '500' }}>Prep Time</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Weekly Meal Prep - Day Sections */}
                  {mealPlanResult.plan_type === 'weekly_meal_prep' && mealPlanResult.weekly_plan ? (
                    <>
                      {/* Daily Meal Cards */}
                      {mealPlanResult.weekly_plan.map(day => (
                        <div key={day.day_index} style={{ marginBottom: '24px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '12px',
                            padding: '12px 14px',
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                            borderRadius: '10px',
                            border: '1px solid rgba(102, 126, 234, 0.3)'
                          }}>
                            <span style={{ fontSize: '24px' }}>📅</span>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#667eea' }}>
                              {day.day_label}
                            </span>
                            {day.daily_totals && (
                              <span style={{ 
                                fontSize: '12px', 
                                color: 'var(--text-secondary)', 
                                marginLeft: 'auto',
                                display: 'flex',
                                gap: '10px',
                                fontWeight: '500'
                              }}>
                                <span>{day.daily_totals.calories} kcal</span>
                                <span>P: {day.daily_totals.protein}g</span>
                                <span>C: {day.daily_totals.carbs}g</span>
                                <span>F: {day.daily_totals.fat}g</span>
                              </span>
                            )}
                          </div>
                          
                          {day.meals.map((meal, mealIdx) => (
                            <div key={`${day.day_index}-${mealIdx}`} className="meal-plan-card" style={{
                              marginBottom: '12px',
                              padding: '16px',
                              background: 'var(--card-bg)',
                              borderRadius: '12px',
                              border: '1px solid var(--border-color)'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'flex-start',
                                marginBottom: '10px'
                              }}>
                                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                  {meal.title}
                                </h4>
                                <span style={{ 
                                  fontSize: '13px', 
                                  fontWeight: '600', 
                                  color: '#4CAF50',
                                  background: 'rgba(76, 175, 80, 0.1)',
                                  padding: '4px 8px',
                                  borderRadius: '6px'
                                }}>
                                  {meal.macros?.calories} kcal
                                </span>
                              </div>
                              
                              <div style={{ 
                                display: 'flex', 
                                gap: '12px', 
                                marginBottom: '12px',
                                fontSize: '12px',
                                color: 'var(--text-secondary)',
                                fontWeight: '500'
                              }}>
                                <span>🥩 {meal.macros?.protein}g protein</span>
                                <span>🍚 {meal.macros?.carbs}g carbs</span>
                                <span>🥑 {meal.macros?.fat}g fat</span>
                              </div>
                              
                              {/* Batch Freeze Tags - Show storage info */}
                              {(meal.servings_total || meal.freezer_friendly) && (
                                <div style={{ 
                                  display: 'flex', 
                                  gap: '8px', 
                                  marginBottom: '12px',
                                  flexWrap: 'wrap'
                                }}>
                                  {meal.freezer_friendly && (
                                    <span style={{ 
                                      fontSize: '10px', 
                                      padding: '3px 8px', 
                                      background: 'rgba(0, 188, 212, 0.15)',
                                      color: '#00BCD4',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}>
                                      ❄️ Freezer Friendly
                                    </span>
                                  )}
                                  {meal.servings_fridge > 0 && (
                                    <span style={{ 
                                      fontSize: '10px', 
                                      padding: '3px 8px', 
                                      background: 'rgba(76, 175, 80, 0.15)',
                                      color: '#4CAF50',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}>
                                      🥡 {meal.servings_fridge} in fridge
                                    </span>
                                  )}
                                  {meal.servings_freezer > 0 && (
                                    <span style={{ 
                                      fontSize: '10px', 
                                      padding: '3px 8px', 
                                      background: 'rgba(33, 150, 243, 0.15)',
                                      color: '#2196F3',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}>
                                      ❄️ {meal.servings_freezer} in freezer
                                    </span>
                                  )}
                                  {meal.storage?.fridge_days && (
                                    <span style={{ 
                                      fontSize: '10px', 
                                      padding: '3px 8px', 
                                      background: 'rgba(255, 152, 0, 0.15)',
                                      color: '#FF9800',
                                      borderRadius: '4px'
                                    }}>
                                      📅 Fridge: {meal.storage.fridge_days}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              {/* Fresh Add-ons for batch freeze meals */}
                              {meal.fresh_add_ons && (
                                <div style={{ 
                                  background: 'rgba(139, 195, 74, 0.1)', 
                                  border: '1px solid rgba(139, 195, 74, 0.2)',
                                  borderRadius: '6px',
                                  padding: '8px 10px',
                                  marginBottom: '12px',
                                  fontSize: '11px',
                                  color: 'var(--text-primary)'
                                }}>
                                  🥬 <strong>Add fresh when serving:</strong> {meal.fresh_add_ons}
                                </div>
                              )}
                              
                              {meal.health_aware_choices && (
                                <div style={{ 
                                  background: 'rgba(76, 175, 80, 0.08)', 
                                  border: '1px solid rgba(76, 175, 80, 0.2)',
                                  borderRadius: '8px',
                                  padding: '10px',
                                  marginBottom: '12px',
                                  fontSize: '12px',
                                  color: 'var(--text-primary)'
                                }}>
                                  💚 {meal.health_aware_choices}
                                </div>
                              )}
                              
                              {meal.reheat_notes && (
                                <div style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '12px',
                                  color: '#ff9800',
                                  marginBottom: '12px'
                                }}>
                                  🔥 <strong>Reheat:</strong> {meal.reheat_notes}
                                </div>
                              )}
                              
                              {meal.ingredients && meal.ingredients.length > 0 && (
                                <details style={{ marginBottom: '10px' }}>
                                  <summary style={{ 
                                    cursor: 'pointer', 
                                    fontWeight: '600', 
                                    fontSize: '13px',
                                    color: 'var(--text-primary)',
                                    marginBottom: '8px' 
                                  }}>
                                    📖 View Ingredients & Instructions
                                  </summary>
                                  <div style={{ paddingLeft: '12px', fontSize: '13px', lineHeight: '1.6' }}>
                                    <p style={{ margin: '8px 0', fontWeight: '600' }}>Ingredients:</p>
                                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                      {meal.ingredients.map((ing, i) => (
                                        <li key={i} style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>{ing}</li>
                                      ))}
                                    </ul>
                                    {meal.instructions && (
                                      <>
                                        <p style={{ margin: '12px 0 8px', fontWeight: '600' }}>Instructions:</p>
                                        <p style={{ color: 'var(--text-secondary)', margin: 0, whiteSpace: 'pre-line' }}>
                                          {formatMealInstructions(meal.instructions)}
                                        </p>
                                      </>
                                    )}
                                  </div>
                                </details>
                              )}
                              
                              <button
                                className="btn-primary"
                                onClick={() => addMealPlanToToday({
                                  dish_name: meal.title || meal.dish_name,
                                  calories: meal.calories ?? meal.macros?.calories ?? 0,
                                  protein: meal.protein ?? meal.macros?.protein ?? 0,
                                  carbs: meal.carbs ?? meal.macros?.carbs ?? 0,
                                  fat: meal.fat ?? meal.macros?.fat ?? 0,
                                  fiber: meal.fiber ?? meal.macros?.fiber ?? 0,
                                  sugar: meal.sugar ?? meal.macros?.sugar ?? 0,
                                  sodium: meal.sodium ?? meal.macros?.sodium ?? 0,
                                  potassium: meal.potassium ?? meal.macros?.potassium ?? 0,
                                  cholesterol: meal.cholesterol ?? meal.macros?.cholesterol ?? 0,
                                  vitamin_a: meal.vitamin_a ?? meal.macros?.vitamin_a ?? 0,
                                  vitamin_c: meal.vitamin_c ?? meal.macros?.vitamin_c ?? 0,
                                  vitamin_d: meal.vitamin_d ?? meal.macros?.vitamin_d ?? 0,
                                  calcium: meal.calcium ?? meal.macros?.calcium ?? 0,
                                  iron: meal.iron ?? meal.macros?.iron ?? 0,
                                  meal_type: meal.meal_type || 'Other',
                                  recipe: {
                                    ingredients: Array.isArray(meal.ingredients) ? meal.ingredients.join('\n') : meal.recipe?.ingredients,
                                    instructions: meal.instructions || meal.recipe?.instructions
                                  }
                                })}
                                style={{ marginTop: '8px', width: '100%', fontSize: '13px' }}
                              >
                                ➕ Log This Meal
                              </button>
                              
                              {/* Save Recipe Button - Save to Smart Hub without logging */}
                              {meal.ingredients && meal.ingredients.length > 0 && (
                                <button
                                  onClick={() => handleSaveSmartMeal(meal, `${day.day_index}_${mealIdx}`)}
                                  disabled={savingSmartMealId === `${day.day_index}_${mealIdx}`}
                                  style={{ 
                                    marginTop: '8px', 
                                    width: '100%', 
                                    fontSize: '13px',
                                    padding: '10px 16px',
                                    background: savingSmartMealId === `${day.day_index}_${mealIdx}` 
                                      ? 'rgba(102, 126, 234, 0.3)' 
                                      : 'rgba(102, 126, 234, 0.15)',
                                    border: '1px solid rgba(102, 126, 234, 0.3)',
                                    borderRadius: '8px',
                                    color: '#667eea',
                                    fontWeight: '600',
                                    cursor: savingSmartMealId === `${day.day_index}_${mealIdx}` ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  {savingSmartMealId === `${day.day_index}_${mealIdx}` ? (
                                    <>Saving...</>
                                  ) : (
                                    <>📖 Save Recipe</>
                                  )}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                      
                      {/* Grocery List Section */}
                      {mealPlanResult.grocery_list && mealPlanResult.grocery_list.length > 0 && (
                        <div style={{
                          marginTop: '24px',
                          padding: '16px',
                          background: 'rgba(102, 126, 234, 0.08)',
                          border: '1px solid rgba(102, 126, 234, 0.2)',
                          borderRadius: '12px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            marginBottom: '16px'
                          }}>
                            <span style={{ fontSize: '24px' }}>🛒</span>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#667eea' }}>
                              Shopping List
                            </span>
                            <span style={{ 
                              fontSize: '11px', 
                              color: 'var(--text-secondary)', 
                              marginLeft: 'auto',
                              fontWeight: '500'
                            }}>
                              {mealPlanResult.grocery_list.length} items
                            </span>
                          </div>
                          
                          {/* Group by category */}
                          {['protein', 'carbs', 'vegetables', 'dairy', 'pantry', 'other'].map(category => {
                            const items = mealPlanResult.grocery_list.filter(item => item.category === category);
                            if (items.length === 0) return null;
                            
                            const categoryLabels = {
                              protein: '🥩 Proteins',
                              carbs: '🍚 Carbs & Grains',
                              vegetables: '🥦 Vegetables',
                              dairy: '🧀 Dairy',
                              pantry: '🫒 Pantry',
                              other: '📦 Other'
                            };
                            
                            return (
                              <div key={category} style={{ marginBottom: '14px' }}>
                                <div style={{ 
                                  fontSize: '13px', 
                                  fontWeight: '600', 
                                  color: '#667eea',
                                  marginBottom: '8px'
                                }}>
                                  {categoryLabels[category]}
                                </div>
                                {items.map((item, idx) => (
                                  <div key={idx} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px 12px',
                                    background: 'rgba(102, 126, 234, 0.1)',
                                    border: '1px solid rgba(102, 126, 234, 0.15)',
                                    borderRadius: '8px',
                                    marginBottom: '6px',
                                    fontSize: '13px'
                                  }}>
                                    <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>☐ {item.item}</span>
                                    <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>{item.quantity}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Bulk Prep Instructions */}
                      {mealPlanResult.bulk_prep_instructions && mealPlanResult.bulk_prep_instructions.length > 0 && (
                        <div style={{
                          marginTop: '20px',
                          padding: '16px',
                          background: 'rgba(255, 152, 0, 0.08)',
                          border: '1px solid rgba(255, 152, 0, 0.2)',
                          borderRadius: '12px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            marginBottom: '16px'
                          }}>
                            <span style={{ fontSize: '24px' }}>👨‍🍳</span>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#ff9800' }}>
                              Bulk Prep Instructions
                            </span>
                          </div>
                          <div style={{ 
                            fontSize: '13px', 
                            lineHeight: '1.8',
                            color: 'var(--text-primary)',
                            whiteSpace: 'pre-line'
                          }}>
                            {mealPlanResult.bulk_prep_instructions.join('\n')}
                          </div>
                        </div>
                      )}
                      
                      {/* ═══════════════════════════════════════════════════════════════
                          BATCH COOK + FREEZE MODE SECTIONS
                      ═══════════════════════════════════════════════════════════════ */}
                      
                      {/* Prep Day Plan - Parallelized steps */}
                      {mealPlanResult.prep_day_plan && mealPlanResult.prep_day_plan.length > 0 && (
                        <div style={{
                          marginTop: '20px',
                          padding: '16px',
                          background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(3, 169, 244, 0.08) 100%)',
                          border: '1px solid rgba(33, 150, 243, 0.3)',
                          borderRadius: '12px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            marginBottom: '16px'
                          }}>
                            <span style={{ fontSize: '24px' }}>📋</span>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#2196F3' }}>
                              Prep Day Plan
                            </span>
                            {mealPlanResult.total_prep_time && (
                              <span style={{ 
                                fontSize: '11px', 
                                color: 'var(--text-primary)', 
                                marginLeft: 'auto',
                                padding: '4px 8px',
                                background: 'rgba(33, 150, 243, 0.15)',
                                borderRadius: '4px',
                                fontWeight: '500'
                              }}>
                                ⏱️ {mealPlanResult.total_prep_time}
                              </span>
                            )}
                          </div>
                          {mealPlanResult.prep_day_plan.map((step, idx) => (
                            <div key={idx} style={{
                              display: 'flex',
                              gap: '12px',
                              padding: '12px',
                              background: 'rgba(33, 150, 243, 0.08)',
                              border: '1px solid rgba(33, 150, 243, 0.15)',
                              borderRadius: '8px',
                              marginBottom: '8px',
                              alignItems: 'flex-start'
                            }}>
                              <div style={{
                                minWidth: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                background: step.parallel ? '#ff9800' : '#2196F3',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: '700',
                                fontSize: '12px'
                              }}>
                                {step.parallel ? '⟷' : idx + 1}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                                  {step.action}
                                </div>
                                {step.time && (
                                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                                    ⏱️ {step.time} {step.parallel && <span style={{ color: '#ff9800' }}>• Can do while waiting</span>}
                                  </div>
                                )}
                                {step.tip && (
                                  <div style={{ fontSize: '11px', color: '#2196F3', marginTop: '4px' }}>
                                    💡 {step.tip}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Freezer Plan */}
                      {mealPlanResult.freezer_plan && mealPlanResult.freezer_plan.length > 0 && (
                        <div style={{
                          marginTop: '20px',
                          padding: '16px',
                          background: 'linear-gradient(135deg, rgba(0, 188, 212, 0.08) 0%, rgba(0, 150, 136, 0.08) 100%)',
                          border: '1px solid rgba(0, 188, 212, 0.3)',
                          borderRadius: '12px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            marginBottom: '16px'
                          }}>
                            <span style={{ fontSize: '24px' }}>❄️</span>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#00BCD4' }}>
                              Freezer Plan
                            </span>
                          </div>
                          {mealPlanResult.freezer_plan.map((item, idx) => (
                            <div key={idx} style={{
                              padding: '12px',
                              background: 'rgba(255,255,255,0.5)',
                              borderRadius: '8px',
                              marginBottom: '8px'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginBottom: '6px'
                              }}>
                                <span style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>
                                  {item.recipe_name}
                                </span>
                                <span style={{ 
                                  fontSize: '11px', 
                                  padding: '3px 8px',
                                  background: 'rgba(0, 188, 212, 0.2)',
                                  color: '#00838F',
                                  borderRadius: '4px',
                                  fontWeight: '600'
                                }}>
                                  {item.portions} portions
                                </span>
                              </div>
                              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <span>📦 {item.container_type || 'Freezer bags/containers'}</span>
                                <span>❄️ Freezes {item.freezer_months || '2-3'} months</span>
                              </div>
                              {item.thaw_instructions && (
                                <div style={{ fontSize: '11px', color: '#00838F', marginTop: '6px' }}>
                                  🔄 Thaw: {item.thaw_instructions}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Reheat Notes Summary */}
                      {mealPlanResult.reheat_notes && mealPlanResult.reheat_notes.length > 0 && (
                        <div style={{
                          marginTop: '20px',
                          padding: '16px',
                          background: 'rgba(255, 87, 34, 0.08)',
                          border: '1px solid rgba(255, 87, 34, 0.2)',
                          borderRadius: '12px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            marginBottom: '16px'
                          }}>
                            <span style={{ fontSize: '24px' }}>🔥</span>
                            <span style={{ fontSize: '16px', fontWeight: '700', color: '#FF5722' }}>
                              Reheat Guide
                            </span>
                          </div>
                          {mealPlanResult.reheat_notes.map((note, idx) => (
                            <div key={idx} style={{
                              padding: '10px 12px',
                              background: 'rgba(255, 87, 34, 0.1)',
                              border: '1px solid rgba(255, 87, 34, 0.15)',
                              borderRadius: '6px',
                              marginBottom: '6px',
                              fontSize: '12px'
                            }}>
                              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{note.recipe_name}:</span>{' '}
                              <span style={{ color: 'var(--text-secondary)' }}>{note.method}</span>
                              {note.tip && <span style={{ color: '#FF5722' }}> • {note.tip}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Storage Tips */}
                      <div style={{
                        marginTop: '20px',
                        padding: '14px',
                        background: 'rgba(156, 39, 176, 0.08)',
                        border: '1px solid rgba(156, 39, 176, 0.2)',
                        borderRadius: '10px',
                        fontSize: '12px',
                        color: 'var(--text-primary)'
                      }}>
                        <strong style={{ color: '#9C27B0' }}>💡 Storage Tips:</strong>
                        <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', lineHeight: '1.6' }}>
                          <li>Store meals in airtight containers</li>
                          <li>Refrigerate and consume within 4-5 days</li>
                          <li>Keep sauces/dressings separate until serving</li>
                          <li>Label containers with day numbers for easy tracking</li>
                        </ul>
                      </div>
                    </>
                  ) : mealPlanResult.num_days > 1 && mealPlanResult.meals_by_day ? (
                    // Display meals organized by day
                    Object.keys(mealPlanResult.meals_by_day).sort((a, b) => parseInt(a) - parseInt(b)).map(dayNum => (
                      <div key={dayNum} style={{ marginBottom: '24px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          marginBottom: '12px',
                          padding: '10px 14px',
                          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                          borderRadius: '8px',
                          border: '1px solid rgba(102, 126, 234, 0.3)'
                        }}>
                          <span style={{ fontSize: '24px' }}>📅</span>
                          <span style={{ fontSize: '16px', fontWeight: '700', color: '#667eea' }}>
                            Day {dayNum}
                          </span>
                          {mealPlanResult.daily_totals_by_day?.[dayNum] && (
                            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                              {mealPlanResult.daily_totals_by_day[dayNum].calories} kcal
                            </span>
                          )}
                        </div>
                        
                        {mealPlanResult.meals_by_day[dayNum].map((meal, idx) => (
                          <div key={`${dayNum}-${idx}`} className="meal-plan-card">
                            <h4 className="meal-plan-meal-name">{meal.meal_type}</h4>
                            <h5 className="meal-plan-dish-name">{meal.dish_name}</h5>
                            <p className="meal-plan-cuisine">Cuisine: {meal.cuisine}</p>
                            
                            {meal.meal_timing && (
                              <p style={{ 
                                fontSize: '13px', 
                                color: '#ff9800', 
                                margin: '8px 0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                              }}>
                                ⏰ <strong>Suggested Time:</strong> {meal.meal_timing}
                              </p>
                            )}
                            
                            <div className="meal-plan-nutrition">
                              <span>Calories: {meal.calories} kcal</span>
                              <span>Protein: {meal.protein}g</span>
                              <span>Carbs: {meal.carbs}g</span>
                              <span>Fat: {meal.fat}g</span>
                            </div>
                            
                            {(meal.health_note || meal.medical_note) && (
                              <div style={{ 
                                background: 'rgba(76, 175, 80, 0.1)', 
                                border: '1px solid rgba(76, 175, 80, 0.3)',
                                borderRadius: '8px',
                                padding: '12px',
                                marginTop: '12px',
                                marginBottom: '12px'
                              }}>
                                <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
                                  <strong>💚 Health-Aware Choices:</strong> {meal.health_note || meal.medical_note}
                                </p>
                              </div>
                            )}

                            {meal.timing_note && (
                              <div data-testid="meal-timing-note" style={{ 
                                background: 'rgba(102, 126, 234, 0.08)', 
                                border: '1px solid rgba(102, 126, 234, 0.25)',
                                borderRadius: '8px',
                                padding: '12px',
                                marginTop: meal.health_note ? '0' : '12px',
                                marginBottom: '12px'
                              }}>
                                <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', color: 'var(--text-primary)' }}>
                                  <strong style={{ color: '#667eea' }}>💊 Timing Note:</strong> {meal.timing_note}
                                </p>
                              </div>
                            )}
                            
                            {meal.recipe && (
                              <details className="meal-plan-recipe">
                                <summary style={{ cursor: 'pointer', fontWeight: '600', marginBottom: '8px' }}>📖 View Recipe</summary>
                                <p><strong>Ingredients:</strong></p>
                                <p className="recipe-text">{meal.recipe.ingredients}</p>
                                <p><strong>Instructions:</strong></p>
                                <p className="recipe-text">{meal.recipe.instructions}</p>
                              </details>
                            )}
                            
                            <button
                              className="btn-primary"
                              onClick={() => addMealPlanToToday(meal)}
                              style={{ marginTop: '16px', width: '100%' }}
                            >
                              ➕ Add to Today's Meals
                            </button>
                            
                            {/* Save Recipe Button - Save to Smart Hub */}
                            {meal.recipe && (
                              <button
                                onClick={() => handleSaveSmartMeal({
                                  ...meal,
                                  title: meal.dish_name,
                                  ingredients: meal.recipe?.ingredients?.split('\n').filter(s => s.trim()) || [],
                                  instructions: meal.recipe?.instructions
                                }, `day${dayNum}_${idx}`)}
                                disabled={savingSmartMealId === `day${dayNum}_${idx}`}
                                style={{ 
                                  marginTop: '8px', 
                                  width: '100%', 
                                  fontSize: '13px',
                                  padding: '10px 16px',
                                  background: savingSmartMealId === `day${dayNum}_${idx}` 
                                    ? 'rgba(102, 126, 234, 0.3)' 
                                    : 'rgba(102, 126, 234, 0.15)',
                                  border: '1px solid rgba(102, 126, 234, 0.3)',
                                  borderRadius: '8px',
                                  color: '#667eea',
                                  fontWeight: '600',
                                  cursor: savingSmartMealId === `day${dayNum}_${idx}` ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                {savingSmartMealId === `day${dayNum}_${idx}` ? (
                                  <>Saving...</>
                                ) : (
                                  <>📖 Save Recipe</>
                                )}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    // Single day display (backward compatibility)
                    mealPlanResult.meals && mealPlanResult.meals.map((meal, idx) => (
                    <div key={idx} className="meal-plan-card">
                      <h4 className="meal-plan-meal-name">{meal.meal_type}</h4>
                      <h5 className="meal-plan-dish-name">{meal.dish_name}</h5>
                      <p className="meal-plan-cuisine">Cuisine: {meal.cuisine}</p>
                      
                      {/* Meal Timing for Fasting */}
                      {meal.meal_timing && (
                        <p style={{ 
                          fontSize: '13px', 
                          color: '#ff9800', 
                          margin: '8px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          ⏰ <strong>Suggested Time:</strong> {meal.meal_timing}
                        </p>
                      )}
                      
                      <div className="meal-plan-nutrition">
                        <span>Calories: {meal.calories} kcal</span>
                        <span>Protein: {meal.protein}g</span>
                        <span>Carbs: {meal.carbs}g</span>
                        <span>Fat: {meal.fat}g</span>
                      </div>
                      
                      {(meal.health_note || meal.medical_note) && (
                        <div style={{ 
                          background: 'rgba(76, 175, 80, 0.1)', 
                          border: '1px solid rgba(76, 175, 80, 0.3)',
                          borderRadius: '8px',
                          padding: '12px',
                          marginTop: '12px',
                          marginBottom: '12px'
                        }}>
                          <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
                            <strong>💚 Health-Aware Choices:</strong> {meal.health_note || meal.medical_note}
                          </p>
                          <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            Note: General awareness only, not medical advice.
                          </p>
                        </div>
                      )}

                      {meal.timing_note && (
                        <div data-testid="meal-timing-note" style={{ 
                          background: 'rgba(102, 126, 234, 0.08)', 
                          border: '1px solid rgba(102, 126, 234, 0.25)',
                          borderRadius: '8px',
                          padding: '12px',
                          marginTop: meal.health_note ? '0' : '12px',
                          marginBottom: '12px'
                        }}>
                          <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', color: 'var(--text-primary)' }}>
                            <strong style={{ color: '#667eea' }}>💊 Timing Note:</strong> {meal.timing_note}
                          </p>
                          <p style={{ margin: '6px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            Awareness only — consult your healthcare provider.
                          </p>
                        </div>
                      )}
                      
                      {meal.recipe && (
                        <div className="meal-plan-recipe">
                          <p><strong>Ingredients:</strong></p>
                          <p className="recipe-text">{meal.recipe.ingredients}</p>
                          <p><strong>Instructions:</strong></p>
                          <p className="recipe-text">{meal.recipe.instructions}</p>
                        </div>
                      )}
                      
                      <button
                        className="btn-primary"
                        onClick={() => addMealPlanToToday(meal)}
                        style={{ marginTop: '16px', width: '100%' }}
                      >
                        ➕ Add to Today's Meals
                      </button>
                      
                      {/* Save Recipe Button - Save to Smart Hub */}
                      {meal.recipe && (
                        <button
                          onClick={() => handleSaveSmartMeal({
                            ...meal,
                            title: meal.dish_name,
                            ingredients: meal.recipe?.ingredients?.split('\n').filter(s => s.trim()) || [],
                            instructions: meal.recipe?.instructions
                          }, `single_${idx}`)}
                          disabled={savingSmartMealId === `single_${idx}`}
                          style={{ 
                            marginTop: '8px', 
                            width: '100%', 
                            fontSize: '13px',
                            padding: '10px 16px',
                            background: savingSmartMealId === `single_${idx}` 
                              ? 'rgba(102, 126, 234, 0.3)' 
                              : 'rgba(102, 126, 234, 0.15)',
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                            borderRadius: '8px',
                            color: '#667eea',
                            fontWeight: '600',
                            cursor: savingSmartMealId === `single_${idx}` ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          {savingSmartMealId === `single_${idx}` ? (
                            <>Saving...</>
                          ) : (
                            <>📖 Save Recipe</>
                          )}
                        </button>
                      )}
                    </div>
                  ))
                  )}
                  
                  <div className="meal-plan-totals">
                    <h4>{mealPlanResult.num_days > 1 ? `Average Daily Totals (${mealPlanResult.num_days} days)` : 'Daily Totals'}</h4>
                    <p>Calories: {mealPlanResult.daily_totals?.calories || 0} kcal</p>
                    <p>Protein: {mealPlanResult.daily_totals?.protein || 0}g</p>
                    <p>Carbs: {mealPlanResult.daily_totals?.carbs || 0}g</p>
                    <p>Fat: {mealPlanResult.daily_totals?.fat || 0}g</p>
                  </div>
                  
                  <AITransparencyNotice />
                  
                  <button 
                    className="btn-secondary full-width"
                    onClick={() => {
                      setMealPlanResult(null);
                    }}
                    style={{ marginTop: '16px' }}
                  >
                    Generate New Plan
                  </button>
                </div>
              )}

              </>
              )}

              <button className="btn-secondary full-width" onClick={() => {
                if (!mealPlanResult) {
                  markMealPlannerExitWithoutGenerate();
                  setMealPlannerJustExited(true);
                }
                onClose();
                setMealPlannerFitMode(false);
                setFitMacroResults(null);
                setMealPlanResult(null);
              }}>
                Close
              </button>
            </div>
          </div>

  );
};

export default SmartMealsModal;
